import type { Agent, AgentOutputType } from './agent';
import type { RunContext } from './run-context';
import { RuntimeEventEmitter } from './shims/event-emitter';
import type { EventEmitter, EventEmitterEvents } from './shims/interface';
import type { Tool } from './tool';
import type { AgentInputItem, TextOutput, UnknownContext } from './types';
import * as protocol from './types/protocol';

export abstract class EventEmitterDelegate<
  EventTypes extends EventEmitterEvents = Record<string, any[]>,
> implements EventEmitter<EventTypes>
{
  protected abstract eventEmitter: EventEmitter<EventTypes>;

  on<K extends keyof EventTypes>(
    type: K,
    listener: (...args: EventTypes[K]) => void,
  ): EventEmitter<EventTypes> {
    this.eventEmitter.on(type, listener);
    return this.eventEmitter;
  }
  off<K extends keyof EventTypes>(
    type: K,
    listener: (...args: EventTypes[K]) => void,
  ): EventEmitter<EventTypes> {
    this.eventEmitter.off(type, listener);
    return this.eventEmitter;
  }
  emit<K extends keyof EventTypes>(type: K, ...args: EventTypes[K]): boolean {
    return this.eventEmitter.emit(type, ...args);
  }
  once<K extends keyof EventTypes>(
    type: K,
    listener: (...args: EventTypes[K]) => void,
  ): EventEmitter<EventTypes> {
    this.eventEmitter.once(type, listener);
    return this.eventEmitter;
  }
}

export type AgentHookEvents<
  TContext = UnknownContext,
  TOutput extends AgentOutputType = TextOutput,
> = {
  /**
   * @param context - 运行上下文
   * @param agent - 正在启动的智能体
   * @param turnInput - 当前轮次的输入项
   */
  agent_start: [
    context: RunContext<TContext>,
    agent: Agent<TContext, TOutput>,
    turnInput?: AgentInputItem[],
  ];
  /**
   * 请注意，此处的第二个参数与运行钩子并不一致。
   * 修改该参数列表是一个破坏性变更，因此短期内我们不会对其进行更改。
   * 如果我们重新审视参数的数据结构（例如，迁移为单个对象），
   * 后续可以更容易地添加更多属性。
   *
   * @param context - 运行上下文
   * @param output - 智能体的输出
   */
  agent_end: [context: RunContext<TContext>, output: string];
  /**
   * @param context - 运行上下文
   * @param agent - 正在执行交接的智能体
   * @param nextAgent - 下一个将要运行的智能体
   */
  agent_handoff: [context: RunContext<TContext>, nextAgent: Agent<any, any>];
  /**
   * @param context - 运行上下文
   * @param agent - 正在启动工具的智能体
   * @param tool - 正在启动的工具
   */
  agent_tool_start: [
    context: RunContext<TContext>,
    tool: Tool<any>,
    details: { toolCall: protocol.ToolCallItem },
  ];
  /**
   * @param context - 运行上下文
   * @param agent - 正在结束工具的智能体
   * @param tool - 正在结束的工具
   * @param result - 工具的执行结果
   */
  agent_tool_end: [
    context: RunContext<TContext>,
    tool: Tool<any>,
    result: string,
    details: { toolCall: protocol.ToolCallItem },
  ];
};

/**
 * 每个 Agent 实例都继承自该类的事件发射器，用于发射智能体生命周期中的事件。
 */
export class AgentHooks<
  TContext = UnknownContext,
  TOutput extends AgentOutputType = TextOutput,
> extends EventEmitterDelegate<AgentHookEvents<TContext, TOutput>> {
  protected eventEmitter = new RuntimeEventEmitter<
    AgentHookEvents<TContext, TOutput>
  >();
}

export type RunHookEvents<
  T = UnknownContext,
  P extends AgentOutputType = TextOutput,
> = {
  /**
   * @param context - The context of the run
   * @param agent - The agent that is starting
   */
  agent_start: [
    context: RunContext<T>,
    agent: Agent<T, P>,
    turnInput?: AgentInputItem[],
  ];
  /**
   * @param context - The context of the run
   * @param agent - The agent that is ending
   * @param output - The output of the agent
   */
  agent_end: [context: RunContext<T>, agent: Agent<T, P>, output: string];
  /**
   * @param context - The context of the run
   * @param fromAgent - The agent that is handing off
   * @param toAgent - The next agent to run
   */
  agent_handoff: [
    context: RunContext<T>,
    fromAgent: Agent<any, any>,
    toAgent: Agent<any, any>,
  ];
  /**
   * @param context - The context of the run
   * @param agent - The agent that is starting a tool
   * @param tool - The tool that is starting
   */
  agent_tool_start: [
    context: RunContext<T>,
    agent: Agent<T, P>,
    tool: Tool,
    details: { toolCall: protocol.ToolCallItem },
  ];
  /**
   * @param context - The context of the run
   * @param agent - The agent that is ending a tool
   * @param tool - The tool that is ending
   * @param result - The result of the tool
   */
  agent_tool_end: [
    context: RunContext<T>,
    agent: Agent<T, P>,
    tool: Tool,
    result: string,
    details: { toolCall: protocol.ToolCallItem },
  ];
};

/**
 * 事件发射器，每个 Runner 实例均继承自该类，用于发射整个运行生命周期内的事件。
 */
export class RunHooks<
  TContext = UnknownContext,
  TOutput extends AgentOutputType = TextOutput,
> extends EventEmitterDelegate<RunHookEvents<TContext, TOutput>> {
  protected eventEmitter = new RuntimeEventEmitter<
    RunHookEvents<TContext, TOutput>
  >();
}
